// src/pages/Login.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { getErrorMessage } from '../utils/helpers';
import { startAuthentication } from '@simplewebauthn/browser';

// Check if WebAuthn is supported on this device
const webAuthnSupported = () => {
  return window.PublicKeyCredential !== undefined &&
    typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function';
};

export default function Login() {
  const { login, staff } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [showBiometricEmail, setShowBiometricEmail] = useState(false);
  const [biometricEmail, setBiometricEmail] = useState('');
  const [showPin, setShowPin] = useState(false);
  const pinRef = useRef(null);

  useEffect(() => { if (staff) navigate('/'); }, [staff, navigate]);

  useEffect(() => {
    if (webAuthnSupported()) {
      window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then(available => setBiometricAvailable(available))
        .catch(() => setBiometricAvailable(false));
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError('Please enter your email'); return; }
    if (pin.length !== 6) { setError('PIN must be exactly 6 digits'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/auth/login', { email: email.trim().toLowerCase(), pin });
      login(res.data.token, res.data.staff);
      // Flagged accounts go straight to the PIN form rather than the dashboard.
      navigate(res.data.mustChangePin ? '/profile?changePin=1' : '/');
    } catch (err) {
      setError(getErrorMessage(err));
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  const handleBiometricLogin = async () => {
    const emailToUse = biometricEmail || email;
    if (!emailToUse.trim()) {
      setShowBiometricEmail(true);
      return;
    }
    setBiometricLoading(true);
    setError('');
    try {
      // Get challenge from server
      const optionsRes = await api.post('/auth/webauthn/authenticate-options', {
        email: emailToUse.trim().toLowerCase()
      });

      // Trigger Face ID / Touch ID
      const authResponse = await startAuthentication(optionsRes.data);

      // Verify with server
      const verifyRes = await api.post('/auth/webauthn/authenticate', {
        staffId: optionsRes.data.staffId,
        response: authResponse
      });

      login(verifyRes.data.token, verifyRes.data.staff);
      navigate('/');
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Biometric authentication cancelled or not available on this device.');
      } else if (err.response?.status === 404) {
        setError('No biometric login set up for this account. Please sign in with your PIN first, then enable it in Profile.');
      } else {
        setError(getErrorMessage(err) || 'Biometric login failed. Please use your PIN.');
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  return (
    <div className="login-page">

      {/* ── LEFT PANEL (tablet + desktop) ── */}
      <div className="login-left">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 0 }}>
          <img
            src="/numa_logo_header.png"
            alt="Numa Behavioral Health"
            style={{ width: '75%', maxWidth: 280, height: 'auto', objectFit: 'contain', marginBottom: 16 }}
          />
          <div style={{ color: 'var(--accent)', fontSize: 15, fontStyle: 'italic', letterSpacing: '0.02em', marginBottom: 32 }}>
            healing begins here
          </div>
          <div style={{ width: 40, height: 2, background: 'rgba(255,255,255,0.2)', borderRadius: 1, marginBottom: 32 }} />
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, textAlign: 'center', lineHeight: 1.8, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Digital Rounds<br />
            <span style={{ fontSize: 11, textTransform: 'none', letterSpacing: 0 }}>HIPAA-compliant patient safety logging</span>
          </div>
        </div>
        <div style={{ padding: '0 32px 32px', textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', lineHeight: 1.6 }}>
            Numa Behavioral Health · Hollywood, CA
          </div>
        </div>
      </div>

      {/* ── RIGHT PANEL ── */}
      <div className="login-right">

        {/* Mobile logo */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{ background: 'var(--primary)', borderRadius: 16, padding: '28px 36px 22px', display: 'inline-block' }}>
            <img src="/numa_logo_header.png" alt="Numa" style={{ height: 46, width: 'auto', objectFit: 'contain', display: 'block' }} />
          </div>
          <div style={{ fontSize: 13, color: 'var(--accent)', fontStyle: 'italic', marginTop: 8 }}>healing begins here</div>
          <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 2 }}>Digital Rounds</div>
        </div>

        {/* Sign in card */}
        <div style={{ background: '#fff', border: 'var(--border)', borderRadius: 'var(--radius)', padding: '1.75rem', boxShadow: 'var(--shadow-md)' }}>
          <h1 style={{ fontSize: 20, fontWeight: 600, color: 'var(--primary)', marginBottom: 6 }}>Sign in</h1>
          <p style={{ fontSize: 13, color: 'var(--gray-400)', marginBottom: 20 }}>Enter your work email and 6-digit PIN.</p>

          <form onSubmit={handleSubmit}>
            <div className="field">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                
                autoComplete="email"
                required
                inputMode="email"
                onBlur={() => { if (email && pinRef.current) pinRef.current.focus(); }}
              />
            </div>

            <div className="field">
              <label>6-digit PIN</label>
              <div style={{ position: 'relative' }}>
                <input
                  ref={pinRef}
                  type={showPin ? 'text' : 'password'}
                  inputMode="numeric"
                  value={pin}
                  onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="••••••"
                  maxLength={6}
                  required
                  autoComplete="one-time-code"
                  style={{ letterSpacing: '0.3em', fontSize: 22, textAlign: 'center', paddingRight: 44 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPin(v => !v)}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 4 }}
                >
                  {showPin ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
                {pin.length}/6 digits {pin.length === 6 && '✓'}
              </div>
            </div>

            {error && <div className="alert-bar danger" style={{ marginBottom: 12 }}>{error}</div>}

            <button
              className="btn btn-primary"
              type="submit"
              disabled={loading || pin.length !== 6}
              style={{ fontSize: 15, padding: 14 }}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          {/* Biometric option */}
          {biometricAvailable && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
                <span style={{ fontSize: 12, color: '#9CA3AF' }}>or</span>
                <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
              </div>

              {showBiometricEmail && (
                <div className="field" style={{ marginBottom: 10 }}>
                  <label>Your email (for biometric login)</label>
                  <input
                    type="email"
                    value={biometricEmail}
                    onChange={e => setBiometricEmail(e.target.value)}
                    
                    inputMode="email"
                  />
                </div>
              )}

              <button
                onClick={handleBiometricLogin}
                disabled={biometricLoading}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 500,
                  color: 'var(--primary)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                }}
              >
                {biometricLoading ? (
                  'Authenticating...'
                ) : (
                  <>
                    <FaceIDIcon />
                    Use Face ID / Touch ID
                  </>
                )}
              </button>
              <p style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'center', marginTop: 8 }}>
                Set up biometric login in your Profile after signing in with your PIN.
              </p>
            </div>
          )}
        </div>

        <p style={{ textAlign: 'center', fontSize: 11, color: '#9CA3AF', marginTop: 20, lineHeight: 1.6 }}>
          All activity is logged for HIPAA compliance
        </p>
      </div>
    </div>
  );
}


function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function FaceIDIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3H5a2 2 0 00-2 2v4m6-6h6m-6 0v18m6-18h4a2 2 0 012 2v4M3 9v6m18-6v6M9 21h6m-6 0H5a2 2 0 01-2-2v-4m16 6h4a2 2 0 002-2v-4" />
      <circle cx="9" cy="10" r="1" fill="currentColor" />
      <circle cx="15" cy="10" r="1" fill="currentColor" />
      <path d="M9 15c.5 1.5 5.5 1.5 6 0" />
    </svg>
  );
}
